import { type Integration, IntegrationTarget } from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    getIntegrationInstallationKey,
    type IntegrationInstallationTarget,
} from '@gitbook/app-database';
import { toast } from '@gitbook/spine-react';
import { useEventCallback, useLoadingState } from '@gitbook/util-react-hooks';

import { apiClient } from '../../state';

type UseInstallIntegrationTriggerOptions = {
    site?: string;
    space?: string;
    suppressToast?: boolean;
};

/**
 * Hook that returns a function to install the integration along with a loading state.
 * If a space is passed to the function then the integration will also be installed to that space.
 */
export function useInstallIntegration(
    integration: Integration,
    target: IntegrationInstallationTarget,
    /**
     * Whether the integration is already installed on the target org. If true, the installation
     * will be skipped on the target.
     */
    isInstalledOnOrg: boolean
): [(options?: UseInstallIntegrationTriggerOptions) => Promise<void>, boolean] {
    const [loadingInstall, wrapLoadingInstall] = useLoadingState();

    const install = useEventCallback(async (options?: UseInstallIntegrationTriggerOptions) => {
        const { space, site, suppressToast = false } = options ?? {};

        if (site && space) {
            throw new Error("Can't install integration on both site and space");
        }

        await toast.catch(async () => {
            await wrapLoadingInstall(async () => {
                let currentInstallationId = isInstalledOnOrg
                    ? getIntegrationInstallationKey(integration.name, target)
                    : null;

                if (!currentInstallationId) {
                    const response = await apiClient.integrations.installIntegration(
                        integration.name,
                        {
                            organization: target.organization,
                        }
                    );
                    currentInstallationId = response.data.id;

                    analytics.logEvent({
                        type: 'integration_install',
                        integration: integration.name,
                        organization: target.organization,
                        withSpaceInstallation: !!space,
                    });
                }

                if (
                    space &&
                    (integration.target === IntegrationTarget.Space ||
                        integration.target === IntegrationTarget.All)
                ) {
                    await apiClient.integrations.installIntegrationOnSpace(
                        integration.name,
                        currentInstallationId,
                        { space },
                        { extended: true }
                    );

                    analytics.logEvent({
                        type: 'integration_space_install',
                        integration: integration.name,
                        organization: target.organization,
                        space,
                        withAccountInstallation: !isInstalledOnOrg,
                    });
                }

                if (
                    site &&
                    (integration.target === IntegrationTarget.Site ||
                        integration.target === IntegrationTarget.All)
                ) {
                    await apiClient.integrations.installIntegrationOnSite(
                        integration.name,
                        currentInstallationId,
                        { siteId: site },
                        { extended: true }
                    );

                    analytics.logEvent({
                        type: 'integration_site_install',
                        integration: integration.name,
                        organization: target.organization,
                        site,
                        withAccountInstallation: !isInstalledOnOrg,
                    });
                }
            });
            if (!suppressToast) {
                toast.success(`${integration.title} installed`);
            }
        }, 'Error while installing integration');
    });

    return [install, loadingInstall];
}
